/**
 * React-query hooks for the lessors table — V2 admin.
 *
 * Backed by the supabase client (anon role on the front-end). RLS on lessors
 * requires admin role, so these hooks only succeed for authenticated admin
 * users. Non-admin callers get a permission error from PostgREST and the
 * mutations surface that via the standard react-query error path.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lessor, LessorInput } from "@/types/lessor";
import { fetchLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import { effectiveApartmentOwner } from "@/lib/owner-metrics";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { operatorStamped } from "@/lib/operator-stamped";

const QK = {
  list: (search?: string) => ["v2", "lessors", "list", search ?? ""] as const,
  one: (id: string) => ["v2", "lessors", id] as const,
  openInvoiceCounts: () => ["v2", "lessors", "open-invoice-counts"] as const,
  ownerProfiles: (search?: string) =>
    ["v2", "owner-profiles-for-combobox", search ?? ""] as const,
  apartmentsByLessor: () => ["v2", "lessors", "apartments-by-lessor"] as const,
};

export type OwnerProfile = {
  user_id: string;
  name: string;
  lessor_id: string | null;
};

/**
 * Owner user profiles (role = "owner") with their linked lessor_id.
 * Used in the LessorCombobox to let admins pick an owner by name and
 * resolve to their linked lessor record.
 */
export function useOwnerProfiles(search?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: QK.ownerProfiles(search),
    enabled,
    queryFn: async (): Promise<OwnerProfile[]> => {
      const { data: roles, error: rolesErr } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "owner");
      if (rolesErr) throw rolesErr;

      const ownerIds: string[] = (roles ?? []).map(
        (r: { user_id: string }) => r.user_id
      );
      if (ownerIds.length === 0) return [];

      const [profilesRes, ownersRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, name")
          .in("id", ownerIds)
          .order("name", { ascending: true }),
        supabase
          .from("property_owners")
          .select("user_id, lessor_id")
          .in("user_id", ownerIds),
      ]);

      if (profilesRes.error) throw profilesRes.error;

      const lessorMap = new Map<string, string | null>();
      for (const o of (ownersRes.data ?? []) as Array<{
        user_id: string;
        lessor_id: string | null;
      }>) {
        if (!lessorMap.has(o.user_id))
          lessorMap.set(o.user_id, o.lessor_id ?? null);
      }

      const results: OwnerProfile[] = (profilesRes.data ?? [])
        .map((p: { id: string; name: string | null }) => ({
          user_id: p.id,
          name: p.name ?? p.id,
          lessor_id: lessorMap.get(p.id) ?? null,
        }))
        .filter(
          (o: OwnerProfile) =>
            !search || o.name.toLowerCase().includes(search.toLowerCase())
        );

      return results;
    },
  });
}

/**
 * List all lessors, optionally filtered by case-insensitive name search.
 * Sorted alphabetically by name.
 */
export function useLessors(search?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: QK.list(search),
    enabled,
    queryFn: async (): Promise<Lessor[]> => {
      let q = supabase
        .from("lessors")
        .select("*")
        .order("name", { ascending: true });
      if (search && search.trim()) {
        q = q.ilike("name", `%${search.trim()}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Lessor[];
    },
  });
}

/** Single lessor by id, used by the edit dialog when editing existing rows. */
export function useLessor(id: string | null | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ["v2", "lessors", "noop"],
    queryFn: async (): Promise<Lessor | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("lessors")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Lessor | null;
    },
    enabled: !!id,
  });
}

// useLessorPropertyCounts() lived here: properties-per-lessor, for a badge and
// a delete warning that both now count APARTMENTS instead (ownership is
// COALESCE(apartments.lessor_id, properties.lessor_id), and most owners hold no
// property at all). Removed rather than left as a tempting wrong answer — see
// useOwnersList in useOwnerDetail.ts for the counts the Owners surfaces use.

/** Just enough of an apartment to link to its detail page. */
export type LessorApartmentRef = {
  id: number;
  label: string | null;
  property_id: number;
  property_title: string | null;
};

/**
 * Apartments grouped by their EFFECTIVE lessor —
 * COALESCE(apartments.lessor_id, properties.lessor_id), the same ownership rule
 * the invoice generator and the Owners surfaces bill by.
 *
 * Used to send an owner's name straight to their apartment page. Each list is
 * ordered by property title, then label — the SAME order the owner modal's
 * Apartments tab uses, so "the owner's first apartment" resolves to the same
 * one on every surface.
 */
export function useApartmentsByLessor() {
  return useQuery({
    queryKey: QK.apartmentsByLessor(),
    queryFn: async (): Promise<Record<string, LessorApartmentRef[]>> => {
      const [{ data, error }, lessorByOwnerUser] = await Promise.all([
        supabase
          .from("apartments")
          .select(
            "id, label, property_id, lessor_id, owner_user_id, properties:property_id ( lessor_id, title )"
          ),
        fetchLessorIdByOwnerUser(),
      ]);
      if (error) throw error;
      const byLessor: Record<string, LessorApartmentRef[]> = {};
      for (const a of (data ?? []) as Array<{
        id: number;
        label: string | null;
        property_id: number;
        lessor_id: string | null;
        owner_user_id: string | null;
        properties?: { lessor_id: string | null; title: string | null } | null;
      }>) {
        const owner = effectiveApartmentOwner(
          a.lessor_id,
          a.properties?.lessor_id,
          a.owner_user_id ? lessorByOwnerUser[a.owner_user_id] : null
        );
        if (!owner) continue;
        (byLessor[owner] ??= []).push({
          id: a.id,
          label: a.label,
          property_id: a.property_id,
          property_title: a.properties?.title ?? null,
        });
      }
      for (const list of Object.values(byLessor)) {
        list.sort(
          (x, y) =>
            (x.property_title ?? "").localeCompare(y.property_title ?? "") ||
            (x.label ?? "").localeCompare(y.label ?? "", undefined, {
              numeric: true,
            })
        );
      }
      return byLessor;
    },
  });
}

/**
 * Count of non-canceled management_invoices per lessor. Drives the delete
 * dialog's invoice-orphan warning — lessor_id on management_invoices is
 * ON DELETE SET NULL (not cascaded), so deleting an owner with open invoices
 * would leave real financial records pointing at nothing.
 */
export function useLessorOpenInvoiceCounts() {
  return useQuery({
    queryKey: QK.openInvoiceCounts(),
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from("management_invoices")
        .select("lessor_id")
        .not("lessor_id", "is", null)
        .neq("status", "canceled");
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const row of (data ?? []) as Array<{ lessor_id: string | null }>) {
        if (row.lessor_id)
          counts[row.lessor_id] = (counts[row.lessor_id] ?? 0) + 1;
      }
      return counts;
    },
  });
}

export function useCreateLessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: LessorInput): Promise<Lessor> => {
      const { data, error } = await supabase
        .from("lessors")
        .insert(operatorStamped<"lessors">(input))
        .select()
        .single();
      if (error) throw error;
      return data as Lessor;
    },
    onSuccess: () => {
      invalidateOwnerCaches(qc);
    },
  });
}

/**
 * Update a lessor.
 *
 * This used to read the row first, diff it and write its own `owner_updated`
 * audit entry. The database does that now — see the `lessors_audit` trigger in
 * 20260817110000_owner_activity_captures_every_actor.sql — so the read and the
 * write here would only produce a SECOND, identical entry for every admin edit.
 *
 * The move is not just deduplication. Logging from this hook could only ever
 * cover callers that came through this hook: the owner's own Settings page, the
 * Roles & access dialog and the edge functions all write these records without
 * it, and none of them left a trace. The trigger sees every writer.
 */
export async function updateLessorAndLog(params: {
  id: string;
  input: LessorInput;
  /**
   * Why, when the save REMOVES bank details. The database refuses that write
   * without one (20260817110100) and records it beside the removal, so the
   * caller has to collect it before saving rather than discover the refusal.
   */
  reason?: string | null;
}): Promise<Lessor> {
  const reason = params.reason?.trim();
  if (reason) {
    // Goes through the RPC because the reason has to be set inside the same
    // transaction as the write, and PostgREST gives every request its own.
    const { data, error } = await supabase.rpc("update_lessor_with_reason", {
      _id: params.id,
      _input: params.input,
      _reason: reason,
    });
    if (error) throw error;
    return data as Lessor;
  }

  const { data, error } = await supabase
    .from("lessors")
    .update(params.input)
    .eq("id", params.id)
    .select()
    .single();
  if (error) throw error;
  return data as Lessor;
}

/** Bank fields whose REMOVAL the database requires a reason for. */
const LESSOR_BANK_FIELDS = [
  "bank_iban",
  "bank_bic",
  "bank_holder",
  "bank_name",
] as const;

/**
 * True when this save would CLEAR a bank detail that is currently set.
 *
 * Deliberately mirrors `is_cleared()` in the migration, including treating ""
 * and null alike: the UI has to ask for the reason before the write, and the
 * two answers disagreeing would either surprise an admin with a raw database
 * error or prompt them for a reason nothing needed.
 */
export function removesBankDetails(
  before: Partial<Lessor> | null | undefined,
  after: Partial<LessorInput> | null | undefined
): boolean {
  if (!before || !after) return false;
  const set = (v: unknown) =>
    typeof v === "string" ? v.trim().length > 0 : v != null;
  return LESSOR_BANK_FIELDS.some(
    (f) => set(before[f]) && f in after && !set(after[f])
  );
}

export function useUpdateLessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateLessorAndLog,
    onSuccess: (_data, vars) => {
      invalidateOwnerCaches(qc, vars.id);
    },
  });
}

/**
 * Delete a lessor. Linked apartments AND properties (both FKs are ON DELETE
 * SET NULL) keep existing — their lessor_id becomes NULL, which the V2 admin
 * renders as "no lessor assigned." The delete dialog warns about both counts
 * before calling; apartments matter most, since most owners hold no property.
 *
 * Delegates to the admin_delete_lessor RPC, which does the audit-log insert
 * (the mandatory `reason`, since the record itself is gone after deletion —
 * this is the only trail of who removed which owner and why) and the delete
 * in a single transaction, and blocks server-side when non-canceled
 * management_invoices still reference the lessor.
 */
export function useDeleteLessor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
    }: {
      id: string;
      reason: string;
    }): Promise<void> => {
      const { error } = await supabase.rpc("admin_delete_lessor", {
        _lessor_id: id,
        _reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateOwnerCaches(qc);
      // Apartments and properties keep existing with lessor_id NULL, so the
      // property surfaces now render "no lessor assigned" and must refetch too.
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}
